import {useState, useCallback, useEffect} from "react";
import "../App.css";
import {Helmet} from "react-helmet";
import ExcelJS from "exceljs";
import {useDropzone} from "react-dropzone";
// import axios from "axios";
import "../CSS/programmes/ItemPricing.css";

import {DefaultButton, SubmitButton} from "../components/Buttons.tsx";
import Navbar from "../components/Navbar.tsx";
import {ColorModeIcon} from "../context/ColorModeContext.tsx";
import CustomSelect from "../components/CustomSelect.tsx";


import * as React from 'react';
import CustomTextField from "../components/CustomTextField.tsx";
import SearchableMultiSelect from "../components/SearchableMultiSelect.tsx";
import axios from "axios";
import ItemList from "../components/ItemList.tsx";
import {HooverTooltip} from "../components/InfoField.tsx";
import {Stack} from "@mui/material";

const ItemPricing = () => {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  // States for mandatory information
  const [items, setItems] = useState<{ itemID: number; varID: number }[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const [changeValue, setChangeValue] = useState('');
  const [mathSign, setMathSign] = useState<string>("+");
  const [isAbsoluteChange, setIsAbsoluteChange] = useState(false);

  const [prices, setPrices] = useState<{ value: string | number; label: string }[]>([]);
  // const prices = ["EK", "Ladenpreis", "WebShop", "National", "International", "eBay AT", "OttoMarkets", "UVP"];
  const [selectedPrices, setSelectedPrices] = useState<{ value: string; label: string }[]>([]);

  const [infoMessage, setinfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        };
        const response = await axios.get(`${backendUrl}/item_pricing/get_price_ids_and_names/`, config)
        const formattedData = Object.entries(response.data).map(([value, label]) => ({
          value: value,
          label: String(label)
        }));
        setPrices(formattedData);
      } catch (error) {
        setErrorMessage("Fehler beim laden der Märkte. Bitte lade die Seite erneut oder wende dich an Jonathan.");
      }
    };

    fetchData();
  }, []);


  // ------------------------------------------------ Default Options ------------------------------------------------

  const renderDefaultOptions = () => {
    return (
      <div className="ItemPricingPage">
        <div style={{position: "relative", paddingBottom: "15px", gap: "15px"}}>
          <h3>Ändere mit diesem Programm die Preise von Artikeln! </h3>
          {renderInfoTooltip()}
          <div {...getRootProps()} className="drag-drop-field">
            <input {...getInputProps()} />
            {!uploadedFile && (
              <p>
                Ziehe eine Excel-Datei in dieses Feld oder klicke hinein und
                wähle sie aus!
              </p>
            )}
            {uploadedFile && (
              <div>
                <p>Hochgeladene Datei: {uploadedFile.name}</p>
                <DefaultButton
                  label="Datei löschen"
                  onClick={deleteFile}
                  size="small"
                />
              </div>
            )}
          </div>
          <div>{items.length > 0 && <ItemList items={items}/>}</div>
        </div>
        <div className="PriceIncrease">
          <CustomSelect
            label={mathSign === "+" ? "Preiserhöhung" : "Preissenkung"}
            id={"PriceChange"}
            items={[{value: true, label: "Absolut"}, {value: false, label: "Prozentual"}]}
            inputLabel={mathSign === "+" ? "Preiserhöhung" : "Preissenkung"}
            is_required={true}
            onChange={(e) => {
              setIsAbsoluteChange(e.target.value === "true");
            }}
            value={isAbsoluteChange}
          />
          <CustomSelect
            label="+/-"
            id="sign-selector"
            items={[
              {value: "+", label: "Erhöhen +"},
              {value: "-", label: "Senken -"}
            ]}
            inputLabel="+/-"
            is_required={true}
            value={mathSign}
            onChange={(e) => setMathSign(e.target.value as string)}
            sx={{width: "150px", minWidth: "150px"}} // Keep it small
          />
          <CustomTextField
            type="text"
            label={mathSign === "+" ? "Preiserhöhung" : "Preissenkung"}
            placeholder={mathSign === "+" ? "Preiserhöhung" : "Preissenkung"}
            is_required={true}
            errorText="Dieses Feld muss ausgefüllt sein"
            endAdornment={(isAbsoluteChange) ? "€" : "%"}
            value={changeValue}
            onChange={(e) => setChangeValue(e.target.value)}
            inputMode="decimal"
            sx={{minWidth: "200px"}}
          />
        </div>
        <Stack {...{
          direction: 'row',
          spacing: 1,
          alignItems: 'center',
          justifyContent: 'center',
          sx: {width: '100%',}
        }}>
          <SearchableMultiSelect items={prices} inputLabel="Zu ändernde Preise" selectAllOption={true}
                                 selectAllInitially={true} value={selectedPrices} setValue={setSelectedPrices}/>
          <HooverTooltip
            content={"Falls du hier Preise auswählst, welche bei dem jeweiligen Artikel nicht gesetzt sind, so wird der jeweilige Preis beim Erhöhen ignoriert!"}/>
        </Stack>
      </div>
    );
  };

  // Handler for file input
  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        const file = fileRejections[0].file;
        const s =
          "Bitte lade eine lesbare Excel-Datei hoch! " +
          file.name +
          " ist nicht lesbar.";
        alert(s);
        setErrorMessage(s);
        deleteFile(); // deletion of current accepted file
      } else {
        setErrorMessage("");
        const file = acceptedFiles[0];
        if (file) {
          setUploadedFile(file);
          processFile(file);
          setinfoMessage(null);
        }
      }
    },
    []
  );

  // Function to handle file deletion
  const deleteFile = () => {
    setErrorMessage(null);
    setUploadedFile(null);
    setItems([]);
  };

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "application/vnd.ms-excel": [],
    },
  });

  const processFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const buffer = evt.target?.result;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer as ArrayBuffer);

      // Check if workbook contains any worksheets
      if (workbook.worksheets.length === 0) {
        const err = "Die Excel-Dabei enthält keine worksheets!";
        setErrorMessage(err);
        alert(err);
        throw new Error(err);
      }

      const worksheet = workbook.worksheets[0];
      const itemsData: { itemID: number; varID: number }[] = [];

      let error_rows: string = ""; // For displaying erroneous rows in the excel file

      // Find the item id and var id columns
      const header = worksheet.getRow(1);
      let item_id_col = null;
      let var_id_col = null;
      // Helper to clean up value: cast to string -> lowercase -> remove dots
      const normalize = (val: any) => String(val || "").toLowerCase().replace(/[^a-z0-9]/g, "");

      header.eachCell((cell, colNumber) => {
        const val = normalize(cell.value);
        if (val === "itemid") {
          item_id_col = colNumber;
        } else if (val === "variationid" || val === "varid") {
          var_id_col = colNumber;
        }
      });
      if (item_id_col === null && var_id_col === null) {
        const err_text = "Spalte \"Item.id\" und Spalte \"Variation.id\" konnten nicht ermittelt werden.";
        setErrorMessage(err_text);
        alert(err_text);
      } else if (item_id_col === null) {
        const err_text = "Spalte \"Item.id\" konnte nicht ermittelt werden.";
        setErrorMessage(err_text);
        alert(err_text);
      } else if (var_id_col === null) {
        const err_text = "Spalte \"Variation.id\" konnte nicht ermittelt werden.";
        setErrorMessage(err_text);
        alert(err_text);
      }

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          // Skip header row
          const itemID = row.getCell(item_id_col).value as number;
          const varID = row.getCell(var_id_col).value as number;
          if (varID == null || isNaN(varID) || itemID == null || isNaN(itemID)) {
            error_rows = error_rows + "Reihe " + rowNumber + "  -  itemID: " + itemID + ", varID: " + varID + "\n";
          } else {
            itemsData.push({itemID, varID});
          }
        }
      });
      if (error_rows != "") {
        error_rows =
          "Folgende Reihen enthalten eine nicht-lesbare Item-ID oder Varianten-ID und werden ignoriert! \n" +
          error_rows;
        setErrorMessage(error_rows);
        alert(error_rows);
      }
      setItems(itemsData);
    };
    reader.readAsArrayBuffer(file);
  };

  // ------------------------------------------------ ToolTip ------------------------------------------------
  // Renders layout and content of the 'Info' hover text
  const renderInfoTooltip = () => {
    return (
      <div className="tooltipVis">
        Info
        <div className="tooltipCont" style={{ width: "1000px", maxWidth: "1020px" }}>
          <h3>Mit diesem Programm können Preise angepasst werden.</h3>
          <p>
            Per Drag and Drop wird eine .xlsx-Datei in das eingefärbte Feld gezogen.
            Wichtig ist dabei, dass in der ersten Zeile die Namen der Shops stehen und dass es keine Leerzeilen gibt.
            <br/> <br/>
            Die Spalten müssen die folgenden Namen haben (die Groß- und Kleinschreibung ist dabei wichtig!):
            Item.id, Variation.id, EK, Ladenpreis, International, Webshop, National, eBay AT, UVP, Amazon.fr, Amazon.nl,
            Live-Shopping, OttoMarkets, Mengenrabatt ab 2, Mengenrabatt ab 3.
            <br/> <br/>
            Bei absoluter Preiserhöhung wird die eingegebene Zahl auf die momentanen Preise draufaddiert, bei
            prozentualer Erhöhung werden alle Preise um den Prozentsatz (bis zu zwei Nachkommastellen möglicht) erhöht.
            <br/> <br/>
            Die Testfehler bedeuten:
            <ul>
              <li>Test 1: "EK &lt alle anderen Preise" ist fehlgeschlagen</li>
              <li>Test 2: "UVP &ge alle Ladenpreis,Lebshoppreis" ist fehlgeschlagen</li>
              <li>Test 3: "(Webshoppreis/EK) &ge 1,4" ist fehlgeschlagen</li>
              <li>Test 4: "Webshoppreis &le Nationalpreis,Internationalpreis,Ebaypreis" ist fehlgeschlagen</li>
              <li>Test 5: "alle Preise &gt 0" ist fehlgeschlagen</li>
            </ul>
            <br/>
            Werden nur ausgewählte Preise verändert, so werden die Tests auf den ursprünglichen Werten durchgeführt,
            sollten nicht die beiden verglichenen Werte verändert worden sein.
          </p>
        </div>
      </div>
    );
  };

  // ------------------------------------------------ GO BUTTON ------------------------------------------------

  const handleGoButton = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setinfoMessage("");
    setErrorMessage("");

    if (isAbsoluteChange == null) {
      setinfoMessage(null);
      const err = "Bitte wähle aus, ob die Preiserhöhung absolut oder prozentual erfolgen soll.";
      setErrorMessage(err);
      alert(err);
    }
    if (uploadedFile === null) {
      setinfoMessage("");
      setErrorMessage("Bitte lade eine Excel-Datei hoch");
      return
    }

    const itemData = {items: items, changeValue: changeValue, mathSign: mathSign, isAbsoluteChange: isAbsoluteChange, selectedPrices: selectedPrices};

    const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

    await axios.put(`${backendUrl}/item_pricing/set_prices/`, itemData, config)
      .then((response) => {
        console.log("response successful")
        console.log(response.data)

        setinfoMessage(response.data)
      })
      .catch((err) => {
        const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
          : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : err.response.data?.detail?.toString();
        setErrorMessage(error_str);
      });

  };

  // ------------------------------------------------ Error Message ------------------------------------------------
  const renderErrorMessage = () => {
    if (errorMessage) {
      return (
        <div className="error-message-container">
          {" "}
          <p>{errorMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderInfoMessage = () => {
    if (infoMessage) {
      return (
        <div className="info-message-container">
          {" "}
          <p>{infoMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <>
      <Navbar/>
      <Helmet>
        <title>{"Artikel-Preise"}</title>
      </Helmet>
      {renderDefaultOptions()}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "25px",
          alignItems: "center",
        }}
      >
        <SubmitButton
          onClick={handleGoButton}
          label={
            "Preise von Artikeln ändern"
          }
          size="small"
          type="submit"
          isDisabled={items.length === 0 || selectedPrices.length === 0 || changeValue === null || changeValue == ""}
        />
      </div>
      {renderErrorMessage()}
      {renderInfoMessage()}
      {ColorModeIcon()}
    </>
  );
};

export default ItemPricing;
